/**
 * CloudflareDeployWizard Component
 * Guides users through connecting GitHub and Cloudflare for deployment
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Icon, Text } from './ui/index.jsx';
import { API_CONFIG } from '../config/api.js';

// Deploy API endpoints
const DEPLOY_API = API_CONFIG.endpoints.api.deploy;

// Map service keys to their Cloudflare permission names
const SERVICE_TO_PERMISSION = {
    workers: 'Workers Scripts - Edit',
    pages: 'Cloudflare Pages - Edit',
    d1: 'D1 - Edit',
    kv: 'Workers KV Storage - Edit',
    r2: 'Workers R2 Storage - Edit',
    durableObjects: 'Durable Objects - Edit',
    queues: 'Queues - Edit',
    workersAI: 'Workers AI - Read',
    aiGateway: 'AI Gateway - Edit',
    vectorize: 'Vectorize - Edit',
    hyperdrive: 'Hyperdrive - Edit',
    browserRendering: 'Browser Rendering - Edit',
    cronTriggers: 'Workers Scripts - Edit', // Cron uses Workers permission
    emailWorkers: 'Email Routing Rules - Edit',
    cloudflareAccess: 'Access: Apps and Policies - Edit',
    stream: 'Stream - Edit',
    images: 'Cloudflare Images - Edit',
};

/**
 * Get required permissions based on enabled services
 * Returns array of { name, recommended } objects
 */
function getRequiredPermissions(enabledServices) {
    const permissions = [];

    // API Tokens Read is recommended (faster verification) but not required
    permissions.push({ name: 'API Tokens - Read', recommended: true });

    const seen = new Set();
    for (const service of enabledServices) {
        const perm = SERVICE_TO_PERMISSION[service];
        if (perm && !seen.has(perm)) {
            seen.add(perm);
            permissions.push({ name: perm, recommended: false });
        }
    }
    return permissions;
}

// Free services that REQUIRE manual dashboard enablement (one-time setup)
// Only include services that truly need user action before they can be used
const FREE_SERVICES_NEED_ENABLE = {
    r2: { name: 'R2 Storage', link: 'https://dash.cloudflare.com/', tier: '10GB free, zero egress', dashPath: 'R2 Object Storage' },
    cloudflareAccess: { name: 'Cloudflare Access', link: 'https://one.dash.cloudflare.com', tier: '50 users free', dashPath: 'Zero Trust Dashboard', docLink: 'https://developers.cloudflare.com/cloudflare-one/' },
};

// Paid services (require Workers Paid $5/mo or separate billing)
const PAID_SERVICES = {
    queues: { name: 'Queues', cost: 'Workers Paid ($5/mo)', description: 'Message queues & async processing' },
    stream: { name: 'Stream', cost: 'Pay per minute', description: 'Video storage & delivery' },
    images: { name: 'Images', cost: 'Pay per image', description: 'Image optimization & transformations' },
};

/**
 * Extract Cloudflare service keys from the FINAL techStack
 * Parses strings like "Cloudflare Workers", "Cloudflare D1 (SQLite)", "KV for sessions"
 */
function getEnabledServiceKeys(architectureData) {
    if (!architectureData) return [];

    // The FINAL stack is at analysis.techStack - this is what the user sees
    const techStack = architectureData?.analysis?.techStack;
    if (!techStack) return [];

    const enabledKeys = new Set();

    // Combine all techStack values into one string to search
    const stackText = Object.values(techStack).join(' ').toLowerCase();

    // Check for each Cloudflare service in the stack text
    if (stackText.includes('workers')) enabledKeys.add('workers');
    if (stackText.includes('pages')) enabledKeys.add('pages');
    if (stackText.includes('d1')) enabledKeys.add('d1');
    if (stackText.includes('kv')) enabledKeys.add('kv');
    if (stackText.includes('r2')) enabledKeys.add('r2');
    if (stackText.includes('durable objects')) enabledKeys.add('durableObjects');
    if (stackText.includes('queues')) enabledKeys.add('queues');
    if (stackText.includes('workers ai')) enabledKeys.add('workersAI');
    if (stackText.includes('ai gateway')) enabledKeys.add('aiGateway');
    if (stackText.includes('vectorize')) enabledKeys.add('vectorize');
    if (stackText.includes('hyperdrive')) enabledKeys.add('hyperdrive');
    if (stackText.includes('browser rendering')) enabledKeys.add('browserRendering');
    if (stackText.includes('cron')) enabledKeys.add('cronTriggers');
    if (stackText.includes('email')) enabledKeys.add('emailWorkers');
    if (stackText.includes('access')) enabledKeys.add('cloudflareAccess');
    if (stackText.includes('stream')) enabledKeys.add('stream');
    if (stackText.includes('images')) enabledKeys.add('images');

    return Array.from(enabledKeys);
}

/**
 * Extract all special services from architecture data
 * Returns which free services need enablement and which paid services are used
 */
function extractSpecialServices(architectureData) {
    const result = {
        freeServicesNeeded: [],
        paidServicesNeeded: [],
    };

    // Get all enabled service keys
    const enabledKeys = new Set(getEnabledServiceKeys(architectureData));
    if (enabledKeys.size === 0) return result;

    // Check free services that need enablement
    for (const [key, info] of Object.entries(FREE_SERVICES_NEED_ENABLE)) {
        if (enabledKeys.has(key)) {
            result.freeServicesNeeded.push({ key, ...info });
        }
    }

    // Check paid services
    for (const [key, info] of Object.entries(PAID_SERVICES)) {
        if (enabledKeys.has(key)) {
            result.paidServicesNeeded.push({ key, ...info });
        }
    }

    return result;
}

export default function CloudflareDeployWizard({ onStatusChange, disabled = false, architectureData = null, sessionId = null }) {
    const navigate = useNavigate();

    // Get enabled services and their required permissions
    const enabledServices = getEnabledServiceKeys(architectureData);
    const requiredPermissions = getRequiredPermissions(enabledServices);
    // Extract services that need manual dashboard enablement
    const { freeServicesNeeded, paidServicesNeeded } = extractSpecialServices(architectureData);
    const hasSpecialServices = freeServicesNeeded.length > 0 || paidServicesNeeded.length > 0;
    const [connectionStatus, setConnectionStatus] = useState({
        github: { connected: false, loading: false, user: null, error: null },
        cloudflare: { connected: false, loading: false, account: null, error: null }
    });
    const [showCfGuide, setShowCfGuide] = useState(false);
    const [cfToken, setCfToken] = useState('');
    const [cfAccountId, setCfAccountId] = useState('');
    const [cfEmail, setCfEmail] = useState(''); // For Global API Key
    const [useGlobalKey, setUseGlobalKey] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);

    // Check existing connections on mount
    useEffect(() => {
        checkExistingConnections();
    }, []);

    // Notify parent of status changes
    useEffect(() => {
        const allConnected = connectionStatus.github.connected && connectionStatus.cloudflare.connected;
        onStatusChange?.({
            ready: allConnected,
            github: connectionStatus.github,
            cloudflare: connectionStatus.cloudflare
        });
    }, [connectionStatus, onStatusChange]);

    const checkExistingConnections = async () => {
        try {
            const response = await fetch(DEPLOY_API.connectionStatus, {
                method: 'GET',
                credentials: 'include'
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success) {
                    setConnectionStatus({
                        github: {
                            connected: data.github?.connected || false,
                            loading: false,
                            user: data.github?.user || null,
                            error: null
                        },
                        cloudflare: {
                            connected: data.cloudflare?.connected || false,
                            loading: false,
                            account: data.cloudflare?.account || null,
                            error: null
                        }
                    });
                }
            }
        } catch (error) {
            console.error('Failed to check connections:', error);
        }
    };

    const handleGitHubConnect = () => {
        // Redirect to GitHub OAuth
        setConnectionStatus(prev => ({
            ...prev,
            github: { ...prev.github, loading: true, error: null }
        }));

        // Store return URL for after OAuth
        sessionStorage.setItem('deploy_oauth_return', window.location.href);

        // Redirect to our GitHub OAuth endpoint
        window.location.href = DEPLOY_API.github.oauth;
    };

    const handleGitHubDisconnect = async () => {
        try {
            setConnectionStatus(prev => ({
                ...prev,
                github: { ...prev.github, loading: true }
            }));

            await fetch(DEPLOY_API.github.disconnect, {
                method: 'POST',
                credentials: 'include'
            });

            setConnectionStatus(prev => ({
                ...prev,
                github: { connected: false, loading: false, user: null, error: null }
            }));
        } catch (error) {
            setConnectionStatus(prev => ({
                ...prev,
                github: { ...prev.github, loading: false, error: 'Failed to disconnect' }
            }));
        }
    };

    const handleCfVerify = async () => {
        if (!cfToken.trim() || !cfAccountId.trim()) {
            setConnectionStatus(prev => ({
                ...prev,
                cloudflare: { ...prev.cloudflare, error: 'Please enter both API token and Account ID' }
            }));
            return;
        }

        // Validate email is provided for Global API Key
        if (useGlobalKey && !cfEmail.trim()) {
            setConnectionStatus(prev => ({
                ...prev,
                cloudflare: { ...prev.cloudflare, error: 'Email is required for Global API Key' }
            }));
            return;
        }

        setIsVerifying(true);
        setConnectionStatus(prev => ({
            ...prev,
            cloudflare: { ...prev.cloudflare, loading: true, error: null }
        }));

        try {
            // Get enabled services from architecture to send to verify endpoint
            const enabledServices = getEnabledServiceKeys(architectureData);

            const requestBody = {
                apiToken: cfToken,
                accountId: cfAccountId,
                enabledServices: enabledServices // Tell backend which services to check
            };

            // Add email for Global API Key
            if (useGlobalKey && cfEmail.trim()) {
                requestBody.email = cfEmail.trim();
            }

            console.log('[CloudflareDeployWizard] Verifying with enabled services:', enabledServices);

            const response = await fetch(DEPLOY_API.cloudflare.verify, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(requestBody)
            });

            const data = await response.json();

            if (data.success) {
                setConnectionStatus(prev => ({
                    ...prev,
                    cloudflare: {
                        connected: true,
                        loading: false,
                        account: data.account,
                        error: null
                    }
                }));
                setCfToken(''); // Clear sensitive data from state
                setCfEmail('');
                setShowCfGuide(false);
            } else {
                // Show detailed error message
                const errorMsg = data.message || data.error || 'Invalid token or insufficient permissions';
                console.error('Cloudflare verify error:', data);
                setConnectionStatus(prev => ({
                    ...prev,
                    cloudflare: {
                        connected: false,
                        loading: false,
                        account: null,
                        error: errorMsg
                    }
                }));
            }
        } catch (error) {
            setConnectionStatus(prev => ({
                ...prev,
                cloudflare: {
                    connected: false,
                    loading: false,
                    account: null,
                    error: 'Failed to verify token. Please try again.'
                }
            }));
        } finally {
            setIsVerifying(false);
        }
    };

    const handleCfDisconnect = async () => {
        try {
            setConnectionStatus(prev => ({
                ...prev,
                cloudflare: { ...prev.cloudflare, loading: true }
            }));

            await fetch(DEPLOY_API.cloudflare.disconnect, {
                method: 'POST',
                credentials: 'include'
            });

            setConnectionStatus(prev => ({
                ...prev,
                cloudflare: { connected: false, loading: false, account: null, error: null }
            }));
        } catch (error) {
            setConnectionStatus(prev => ({
                ...prev,
                cloudflare: { ...prev.cloudflare, loading: false, error: 'Failed to disconnect' }
            }));
        }
    };

    const { github, cloudflare } = connectionStatus;
    const allConnected = github.connected && cloudflare.connected;

    return (
        <div className="space-y-6">
            {/* Connection Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* GitHub Card */}
                <div className={`
                    p-5 rounded-xl border transition-all
                    ${github.connected
                        ? 'border-green-500/50 bg-green-500/10'
                        : 'border-white/10 bg-white/5'
                    }
                `}>
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className={`
                                w-10 h-10 rounded-lg flex items-center justify-center
                                ${github.connected ? 'bg-green-500/20' : 'bg-white/10'}
                            `}>
                                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/>
                                </svg>
                            </div>
                            <div>
                                <h4 className="font-semibold text-white">GitHub</h4>
                                <p className="text-xs text-gray-400">Repository access</p>
                            </div>
                        </div>
                        {github.connected && (
                            <span className="flex items-center gap-1 text-xs text-green-400">
                                <Icon name="check" size="xs" />
                                Connected
                            </span>
                        )}
                    </div>

                    {github.connected ? (
                        <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm text-gray-300">
                                <img
                                    src={github.user?.avatar}
                                    alt=""
                                    className="w-5 h-5 rounded-full"
                                />
                                <span>{github.user?.username || github.user?.name}</span>
                            </div>
                            <Button
                                onClick={handleGitHubDisconnect}
                                disabled={github.loading || disabled}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                                Disconnect
                            </Button>
                        </div>
                    ) : (
                        <Button
                            onClick={handleGitHubConnect}
                            disabled={github.loading || disabled}
                            className="w-full bg-white/10 hover:bg-white/20 text-white"
                        >
                            {github.loading ? (
                                <span className="flex items-center gap-2">
                                    <Icon name="spinner" size="sm" className="animate-spin" />
                                    Connecting...
                                </span>
                            ) : (
                                'Connect GitHub'
                            )}
                        </Button>
                    )}

                    {github.error && (
                        <p className="mt-2 text-xs text-red-400">{github.error}</p>
                    )}
                </div>

                {/* Cloudflare Card */}
                <div className={`
                    p-5 rounded-xl border transition-all
                    ${cloudflare.connected
                        ? 'border-green-500/50 bg-green-500/10'
                        : 'border-white/10 bg-white/5'
                    }
                `}>
                    <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <div className={`
                                w-10 h-10 rounded-lg flex items-center justify-center
                                ${cloudflare.connected ? 'bg-green-500/20' : 'bg-white/10'}
                            `}>
                                <svg className="w-6 h-6 text-[#F6821F]" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M16.5088 16.8447c.1475-.5068.0908-.9707-.1553-1.3154-.2246-.3164-.6045-.499-1.0615-.5205l-8.6592-.1123a.1559.1559 0 01-.1333-.0713c-.0283-.0434-.0351-.0986-.0205-.1553.0283-.0986.1182-.1709.2168-.1709l8.7275.1123c.9424.0049 1.9629-.5547 2.3838-1.3252l.5762-1.0557c.0147-.0283.0244-.0615.0283-.0957.0049-.0293.0098-.0596.0098-.0889 0-.4629-.3926-.8388-.876-.8388H4.5874c-.0371 0-.0713.0215-.0898.0547L2.4823 15.3155c-.7383 1.3555-.7822 2.7227-.0938 3.8203.6826 1.086 1.9727 1.7324 3.4463 1.7324h10.9805c.4346 0 .8506-.3311.9463-.7559l.7471-3.2676zM19.7539 8.084c-.2148-2.8672-2.5273-5.1797-5.418-5.4121-.6123-.0488-1.2031.0068-1.7558.1406-.2275.0547-.3135.3232-.1758.5166.5742.8047.9102 1.7812.9102 2.8418 0 .2031-.0107.4023-.0313.5986-.0205.1992.1426.3711.3398.3867.0547.0049.1094.0059.1641.0059 1.4355 0 2.6113 1.126 2.6709 2.5596.0137.3291.2871.5908.6162.6016l.7305.0176c1.2617.0312 2.4004.8848 2.6807 2.1172.2383 1.0479-.1035 2.1289-.8916 2.8125l.8486.0049c1.0967-.5742 1.8994-1.6113 2.0879-2.8916.2246-1.5166-.4443-3.0127-1.7764-3.9999z"/>
                                </svg>
                            </div>
                            <div>
                                <h4 className="font-semibold text-white">Cloudflare</h4>
                                <p className="text-xs text-gray-400">Deployment target</p>
                            </div>
                        </div>
                        {cloudflare.connected && (
                            <span className="flex items-center gap-1 text-xs text-green-400">
                                <Icon name="check" size="xs" />
                                Connected
                            </span>
                        )}
                    </div>

                    {cloudflare.connected ? (
                        <div className="space-y-3">
                            <div className="text-sm text-gray-300">
                                {cloudflare.account?.name || 'Account connected'}
                            </div>
                            <Button
                                onClick={handleCfDisconnect}
                                disabled={cloudflare.loading || disabled}
                                variant="ghost"
                                size="sm"
                                className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                                Disconnect
                            </Button>
                        </div>
                    ) : (
                        <Button
                            onClick={() => setShowCfGuide(true)}
                            disabled={cloudflare.loading || disabled}
                            className="w-full bg-white/10 hover:bg-white/20 text-white"
                        >
                            Connect Cloudflare
                        </Button>
                    )}

                    {cloudflare.error && (
                        <p className="mt-2 text-xs text-red-400">{cloudflare.error}</p>
                    )}
                </div>
            </div>

            {/* Cloudflare Token Guide Modal */}
            {showCfGuide && !cloudflare.connected && (
                <div className="p-6 rounded-xl border border-[#7B61FF]/30 bg-[#7B61FF]/5 space-y-5">
                    <div className="flex items-start justify-between">
                        <h4 className="font-semibold text-white flex items-center gap-2">
                            <Icon name="shield" size="sm" className="text-[#7B61FF]" />
                            Connect Cloudflare Account
                        </h4>
                        <button
                            onClick={() => setShowCfGuide(false)}
                            className="text-gray-400 hover:text-white"
                        >
                            <Icon name="close" size="sm" />
                        </button>
                    </div>

                    {/* Step by step guide */}
                    <div className="space-y-4 text-sm">
                        {/* Option 1: Global API Key */}
                        <div className="p-4 rounded-lg bg-black/30 border border-green-500/20 space-y-3">
                            <div className="flex items-center gap-2">
                                <span className="px-2 py-0.5 text-xs font-bold bg-green-500/20 text-green-400 rounded">EASIEST</span>
                                <p className="font-medium text-gray-200">Option 1: Global API Key</p>
                            </div>
                            <p className="text-gray-400 text-xs">Full access to all resources - perfect for personal use</p>
                            <ol className="list-decimal list-inside space-y-2 text-gray-400">
                                <li>Go to <a href="https://dash.cloudflare.com/profile/api-tokens" target="_blank" rel="noopener noreferrer" className="text-[#7B61FF] hover:underline">Cloudflare API Tokens</a></li>
                                <li>Scroll to <strong>"Global API Key"</strong> section</li>
                                <li>Click <strong>"View"</strong> and copy the key</li>
                            </ol>
                            <p className="text-amber-400/80 text-xs mt-2">⚠️ This key has full account access - keep it safe!</p>
                        </div>

                        {/* Option 2: Use Template */}
                        <div className="p-4 rounded-lg bg-black/30 space-y-3">
                            <div className="flex items-center gap-2">
                                <span className="px-2 py-0.5 text-xs font-bold bg-blue-500/20 text-blue-400 rounded">MORE SECURE</span>
                                <p className="font-medium text-gray-200">Option 2: Scoped API Token</p>
                            </div>
                            <p className="text-gray-400 text-xs">Limited permissions - better for production</p>
                            <ol className="list-decimal list-inside space-y-2 text-gray-400">
                                <li>Go to <a href="https://dash.cloudflare.com/profile/api-tokens" target="_blank" rel="noopener noreferrer" className="text-[#7B61FF] hover:underline">Cloudflare API Tokens</a></li>
                                <li>Click "Create Token"</li>
                                <li>Use <strong>"Edit Cloudflare Workers"</strong> template</li>
                                <li>Click <strong>"+ Add more"</strong> to add: <code className="text-[#7B61FF]">D1 - Edit</code></li>
                                <li>Create token and copy immediately</li>
                            </ol>
                            <div className="mt-2 p-2 rounded bg-black/40">
                                <p className="text-xs text-gray-500 mb-1">Required permissions for your stack:</p>
                                <div className="grid grid-cols-2 gap-1">
                                    {requiredPermissions.map((perm, idx) => (
                                        <div key={idx} className="flex items-center gap-1 text-xs text-gray-400">
                                            <Icon name="check" size="xs" className={perm.recommended ? 'text-amber-400' : 'text-[#7B61FF]'} />
                                            <span>{perm.name}</span>
                                            {perm.recommended && (
                                                <span className="text-[10px] text-amber-400/80">(Recommended)</span>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Account ID */}
                        <div className="p-4 rounded-lg bg-black/30 space-y-3">
                            <p className="font-medium text-gray-200">Find your Account ID:</p>
                            <ol className="list-decimal list-inside space-y-2 text-gray-400">
                                <li>Go to <a href="https://dash.cloudflare.com" target="_blank" rel="noopener noreferrer" className="text-[#7B61FF] hover:underline">Cloudflare Dashboard</a></li>
                                <li>Click <strong>Workers & Pages</strong> in the sidebar</li>
                                <li>Copy <strong>Account ID</strong> from the right panel</li>
                            </ol>
                            <div className="mt-2 p-2 rounded bg-black/40 text-xs text-gray-500">
                                💡 Account ID looks like: <code className="text-gray-400">a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6</code>
                            </div>
                        </div>

                        {/* Free Services Warning - Services that need manual enablement */}
                        {freeServicesNeeded.length > 0 && (
                            <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 space-y-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-amber-400 text-lg">⚠️</span>
                                    <p className="font-medium text-amber-300">Enable these FREE services first:</p>
                                </div>
                                <p className="text-amber-200/70 text-xs">Your architecture uses services that need manual enablement in the Cloudflare dashboard.</p>

                                <div className="space-y-2">
                                    {freeServicesNeeded.map((service) => (
                                        <div key={service.key} className="flex items-center justify-between p-2 rounded bg-black/30">
                                            <div className="flex items-center gap-2">
                                                <span className="text-base">
                                                    {service.key === 'r2' ? '📦' :
                                                     service.key === 'queues' ? '📬' :
                                                     service.key === 'workersAI' ? '🤖' :
                                                     service.key === 'aiGateway' ? '🌐' :
                                                     service.key === 'vectorize' ? '🔍' :
                                                     service.key === 'emailWorkers' ? '📧' :
                                                     service.key === 'cloudflareAccess' ? '🔐' : '⚙️'}
                                                </span>
                                                <div>
                                                    <p className="text-sm font-medium text-white">{service.name}</p>
                                                    <p className="text-xs text-gray-400">{service.tier}</p>
                                                </div>
                                            </div>
                                            <a
                                                href={service.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-3 py-1 text-xs font-medium bg-amber-500/20 text-amber-300 rounded hover:bg-amber-500/30 transition-colors"
                                            >
                                                Enable →
                                            </a>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Paid Services Warning */}
                        {paidServicesNeeded.length > 0 && (
                            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 space-y-3">
                                <div className="flex items-center gap-2">
                                    <span className="text-red-400 text-lg">💰</span>
                                    <p className="font-medium text-red-300">PAID SERVICES DETECTED</p>
                                </div>
                                <p className="text-red-200/70 text-xs">Your architecture requires paid services. You'll need to upgrade your Cloudflare plan.</p>

                                <div className="space-y-2">
                                    {paidServicesNeeded.map((service) => (
                                        <div key={service.key} className="flex items-center justify-between p-2 rounded bg-black/30">
                                            <div className="flex items-center gap-2">
                                                <span className="text-base">
                                                    {service.key === 'durableObjects' ? '⚡' :
                                                     service.key === 'hyperdrive' ? '🔗' :
                                                     service.key === 'browserRendering' ? '📸' :
                                                     service.key === 'stream' ? '🎬' :
                                                     service.key === 'images' ? '🖼️' : '💎'}
                                                </span>
                                                <div>
                                                    <p className="text-sm font-medium text-white">{service.name}</p>
                                                    <p className="text-xs text-gray-400">{service.description}</p>
                                                </div>
                                            </div>
                                            <span className="px-3 py-1 text-xs font-medium bg-red-500/20 text-red-300 rounded">
                                                {service.cost}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                <a
                                    href="https://dash.cloudflare.com/?to=/:account/workers/plans"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-full text-center px-4 py-2 text-sm font-medium bg-red-500/20 text-red-300 rounded hover:bg-red-500/30 transition-colors"
                                >
                                    Upgrade to Workers Paid →
                                </a>
                            </div>
                        )}
                    </div>

                    {/* Token Type Toggle */}
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-black/30">
                        <button
                            type="button"
                            onClick={() => setUseGlobalKey(false)}
                            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                                !useGlobalKey
                                    ? 'bg-[#7B61FF] text-white'
                                    : 'bg-black/30 text-gray-400 hover:text-white'
                            }`}
                        >
                            API Token
                        </button>
                        <button
                            type="button"
                            onClick={() => setUseGlobalKey(true)}
                            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                                useGlobalKey
                                    ? 'bg-[#7B61FF] text-white'
                                    : 'bg-black/30 text-gray-400 hover:text-white'
                            }`}
                        >
                            Global API Key
                        </button>
                    </div>

                    {/* Input fields */}
                    <div className="space-y-4">
                        {useGlobalKey && (
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">
                                    Cloudflare Email
                                </label>
                                <input
                                    type="email"
                                    value={cfEmail}
                                    onChange={(e) => setCfEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:border-[#7B61FF] focus:outline-none focus:ring-1 focus:ring-[#7B61FF]"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                {useGlobalKey ? 'Global API Key' : 'API Token'}
                            </label>
                            <input
                                type="password"
                                value={cfToken}
                                onChange={(e) => setCfToken(e.target.value)}
                                placeholder={useGlobalKey ? 'Enter your Global API Key' : 'Enter your API Token'}
                                className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:border-[#7B61FF] focus:outline-none focus:ring-1 focus:ring-[#7B61FF]"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">
                                Account ID
                            </label>
                            <input
                                type="text"
                                value={cfAccountId}
                                onChange={(e) => setCfAccountId(e.target.value)}
                                placeholder="e.g., a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6"
                                className="w-full px-4 py-3 rounded-lg bg-black/50 border border-white/10 text-white placeholder-gray-500 focus:border-[#7B61FF] focus:outline-none focus:ring-1 focus:ring-[#7B61FF]"
                            />
                        </div>
                    </div>

                    {/* Verify button */}
                    <Button
                        onClick={handleCfVerify}
                        disabled={isVerifying || !cfToken.trim() || !cfAccountId.trim()}
                        className="w-full bg-gradient-to-r from-[#7B61FF] to-[#BA87FF] text-white py-3"
                    >
                        {isVerifying ? (
                            <span className="flex items-center justify-center gap-2">
                                <Icon name="spinner" size="sm" className="animate-spin" />
                                Verifying permissions...
                            </span>
                        ) : (
                            'Verify & Connect'
                        )}
                    </Button>

                    <p className="text-xs text-gray-500 text-center">
                        Your token is encrypted and stored securely. We never store it in plain text.
                    </p>
                </div>
            )}

            {/* Status Summary */}
            {allConnected && (
                <div className="space-y-4">
                    <div className="p-4 rounded-xl border border-green-500/30 bg-green-500/10 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                            <Icon name="check" size="md" className="text-green-400" />
                        </div>
                        <div>
                            <p className="font-medium text-green-300">Ready to deploy!</p>
                            <p className="text-sm text-green-400/70">Both GitHub and Cloudflare are connected</p>
                        </div>
                    </div>

                    {/* Start Building Button */}
                    <Button
                        onClick={() => {
                            if (sessionId) {
                                navigate(`/deploy/${sessionId}`);
                            } else {
                                console.error('No sessionId provided for deployment');
                            }
                        }}
                        disabled={!sessionId || disabled}
                        className="w-full py-4 text-lg font-semibold bg-gradient-to-r from-[#7B61FF] to-[#BA87FF] hover:from-[#8B71FF] hover:to-[#CA97FF] disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Icon name="rocket" size="md" className="mr-2" />
                        Start Building
                    </Button>
                </div>
            )}
        </div>
    );
}
